import Anchor from 'components/Basic/Anchor/Anchor';
import Collapse from 'components/Basic/Collapse/Collapse';
import { ExtendedNextLink } from 'components/Basic/ExtendedNextLink/ExtendedNextLink';
import AlternativeUrls from 'components/Basic/Head/AlternativeUrls/AlternativeUrls';
import { ProductMetadata } from 'components/Basic/Head/ProductMetadata/ProductMetadata';
import Image from 'components/Basic/Image/Image';
import StickyBar from 'components/Basic/StickyBar/StickyBar';
import Table from 'components/Basic/Table/Table';
import { YouTubeVideo } from 'components/Basic/YouTubeVideo/YouTubeVideo';
import { ProductStock } from 'components/Blocks/Product/Availability/ProductStock';
import ProductDetailFlags from 'components/Blocks/Product/Flags/ProductDetailFlags';
import { ProductWishlistButton } from 'components/Blocks/Product/Wishlist/ProductWishlistButtons';
import { ProductLineWrapper } from 'components/Blocks/ProductLine/ProductLineWrapper';
import { ProductReviews } from 'components/Blocks/Reviews/ProductReviews';
import { ReviewSummaryPreview } from 'components/Blocks/Reviews/ReviewSummaryPreview';
import LimitHeightWrap from 'components/Helpers/LimitHeightWrap/LimitHeightWrap';
import UserText from 'components/Helpers/UserText/UserText';
import Webline from 'components/Layout/Webline/Webline';
import { ColorVariants } from 'components/Pages/ProductDetail/ColorVariants/ColorVariants';
import ProductDetailAddToCart from 'components/Pages/ProductDetail/ProductDetailAddToCart/ProductDetailAddToCart';
import ProductDetailBuyInEshop from 'components/Pages/ProductDetail/ProductDetailAddToCart/ProductDetailBuyInEshop';
import { ProductDetailAdvertBanner } from 'components/Pages/ProductDetail/ProductDetailAdvertBanner';
import ProductDetailAvailability from 'components/Pages/ProductDetail/ProductDetailAvailability/ProductDetailAvailability';
import ProductDetailFiles from 'components/Pages/ProductDetail/ProductDetailFiles/ProductDetailFiles';
import { ProductDetailGallery } from 'components/Pages/ProductDetail/ProductDetailGallery';
import ProductDetailRelated from 'components/Pages/ProductDetail/ProductDetailRelated/ProductDetailRelated';
import ProductDetailSpareParts from 'components/Pages/ProductDetail/ProductDetailSpareParts/ProductDetailSpareParts';
import ProductDetailStickyBar from 'components/Pages/ProductDetail/ProductDetailStickyBar/ProductDetailStickyBar';
import { mapAddToCartPopupData } from 'connectors/cart/Cart';
import { removeLinks } from 'helpers/formaters/formatText';
import { getTranslateFromLogicalValue } from 'helpers/getTranslateFromLogicalValue';
import { isB2bDomain, isB2cDomain, isDomainLocale } from 'helpers/isDomainLocale';
import { getUrlWithoutGetParameters } from 'helpers/parsing/urlParsing';
import { useAddToCart } from 'hooks/cart/useAddToCart';
import { useGtmProductDetailView } from 'hooks/gtm/useGtmProductDetailView';
import { useSetLastVisitedProductCatalogNumbers } from 'hooks/lastVisited/useSetLastVisitedProductCatalogNumbers';
import { usePersoo } from 'hooks/persoo/usePersoo';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useEffectOnce } from 'hooks/ui/useEffectOnce';
import { useMediaMin } from 'hooks/ui/useMediaMin';
import { useSticky } from 'hooks/ui/useSticky';
import { useDomainConfig } from 'hooks/useDomainConfig';
import { useWishlist } from 'hooks/wishlist/useWishlist';
import { createPersooEventForAddToCart } from 'modules/persoo/persooEvents';
import { PERSOO_IDS } from 'modules/persoo/persooTypes';
import { Translate } from 'next-translate';
import { useRouter } from 'next/router';
import { ArrowIcon, CheckIcon } from 'public/svg/IconsSvg';
import React, { Dispatch, Fragment, RefObject, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import * as smoothscroll from 'smoothscroll-polyfill';
import { usePersistStore } from 'store/usePersistStore';
import { twJoin } from 'tailwind-merge';
import { ProductDetailType, VariantDetailType } from 'types/product';

type ProductDetailProps = {
    fetching: boolean;
    product: ProductDetailType | VariantDetailType;
};

const TEST_IDENTIFIER = 'pages-productdetail';

const productDetailCodeTwClass = 'text-tiny leading-6 text-primary';
const productNameTwClass = 'text-h4 font-bold text-primary';

const ProductDetail: FC<ProductDetailProps> = ({ fetching, product }) => {
    const t = useTypedTranslationFunction();
    const domainConfig = useDomainConfig();
    const { isPersooAllowed } = usePersoo();

    const scrollTarget = useRef<HTMLUListElement>(null);
    const { asPath } = useRouter();
    useGtmProductDetailView(product, getUrlWithoutGetParameters(asPath), fetching);

    const { currencyCode, type: domainType, defaultLocale } = useDomainConfig();
    const isItalianLocale = isDomainLocale(defaultLocale, 'it');
    const isEnglishLocale = isDomainLocale(defaultLocale, 'en');
    const overviewRef = useRef<HTMLDivElement>(null);
    const filesRef = useRef<HTMLDivElement>(null);
    const sparePartsRef = useRef<HTMLDivElement>(null);
    const productLineRef = useRef<HTMLDivElement>(null);
    const relatedProductsRef = useRef<HTMLDivElement>(null);
    const reviewsRef = useRef<HTMLDivElement | null>(null);
    const toTopContentRef = useRef<HTMLDivElement>(null);
    const [isProductBarSticky, , stickyRef] = useSticky(null, [asPath]);

    const [isOpenOverview, setIsOpenOverview] = useState(false);
    const [isOpenProductFiles, setIsOpenProductFiles] = useState(false);
    const [isOpenReviews, setIsOpenReviews] = useState(false);
    const [isOpenProductSpareParts, setIsOpenProductSpareParts] = useState(false);
    const [isOpenProductLine, setIsOpenProductLine] = useState(false);
    const [heightUpdateCounter, setHeightUpdateCounter] = useState(0);
    const [reviewsHeightUpdateCounter, setReviewsHeightUpdateCounter] = useState(0);
    const [productLineHeightUpdateCounter, setProductLineHeightUpdateCounter] = useState(0);
    const isMobile = !useMediaMin('sm');
    const isTablet = !useMediaMin('lg');

    useSetLastVisitedProductCatalogNumbers(product.catalogNumber);

    const [changeCartItemQuantity, isAddToCartFetching] = useAddToCart('product');
    const { updateUserState } = usePersistStore((s) => s);
    const { toggleProductInWishlist, isProductInWishlist } = useWishlist();

    const onAddToCartHandler = useCallback(async () => {
        const addToCartResult = await changeCartItemQuantity(product.uuid, 1, 1, 'detail');
        const mappedAddToCartResult = mapAddToCartPopupData(addToCartResult, currencyCode);
        if (mappedAddToCartResult) {
            createPersooEventForAddToCart(mappedAddToCartResult.catalogNumber);
            updateUserState({
                addToCartPopupData: {
                    gtmListName: 'detail',
                    listIndex: 1,
                    product: mappedAddToCartResult,
                },
            });
        }
    }, [changeCartItemQuantity, currencyCode, product.uuid, updateUserState]);

    const onAnchorClickHandler = useCallback(
        (
            passedRef: RefObject<HTMLDivElement>,
            setPassedState?: Dispatch<SetStateAction<boolean>>,
            deltaOffsetTop?: number,
        ) => {
            if (typeof setPassedState === 'function') {
                setPassedState(true);
            }

            if (passedRef.current === null) {
                return;
            }

            let targetOffsetTop = passedRef.current.offsetTop;
            targetOffsetTop -= stickyRef?.current?.offsetHeight ?? 0;
            targetOffsetTop -= deltaOffsetTop ?? 0;

            window.scrollTo({ top: targetOffsetTop, behavior: 'smooth' });
        },
        [stickyRef],
    );

    useEffectOnce(() => smoothscroll.polyfill());

    useEffect(() => {
        setIsOpenOverview(isMobile);
    }, [isMobile]);

    return (
        // the key helps to re-mount the component when navigating between different products, which prevents the components from keeping an unwanted state
        <Fragment key={product.uuid}>
            <ProductMetadata product={product} />
            <AlternativeUrls alternativeUrls={product.alternateUrls} />
            <Webline>
                <div
                    className="mb-10 flex flex-col flex-wrap lg:mb-2 lg:flex-row"
                    data-testid={
                        'colorVariants' in product ? `${TEST_IDENTIFIER}-variant-${product.catalogNumber}` : ''
                    }
                >
                    {isTablet && (
                        <h1
                            className={twJoin(productNameTwClass, 'mb-2 lg:hidden')}
                            data-testid={`${TEST_IDENTIFIER}-name`}
                        >
                            {product.name}
                        </h1>
                    )}
                    <div
                        className={twJoin(
                            'relative mb-7 lg:mb-0 lg:w-[calc(100%-346px)] vl:w-[calc(100%-508px)]',
                            '[&>div]:w-full [&>div]:overflow-hidden [&>div]:text-[0]', // this styled is used to style the div inserted by the lightbox gallery package
                        )}
                        data-testid={`${TEST_IDENTIFIER}-gallery`}
                    >
                        <ProductDetailGallery
                            flags={product.flags}
                            images={product.images}
                            productName={product.name}
                            youtubeVideoId={product.youtubeVideoId}
                        />
                        <ProductDetailFlags flags={product.flags} product={product} isProductDetail />
                    </div>
                    <div className="w-full lg:w-[346px] lg:pl-7 vl:w-[508px] vl:pl-10 [&>*:last-child]:mb-0">
                        {!isTablet && (
                            <h1
                                className={twJoin(
                                    productNameTwClass,
                                    'mb-1 hidden lg:block vl:text-[30px] vl:leading-9',
                                )}
                                data-testid={`${TEST_IDENTIFIER}-name`}
                            >
                                {product.name}
                            </h1>
                        )}
                        {product.benefits.length > 0 && product.benefits[0] !== '' && (
                            <ul className="pt-1 pb-2" data-testid={`${TEST_IDENTIFIER}-short-description`}>
                                {product.benefits.map((item: string, key) => (
                                    <li className="mb-2 flex text-body font-bold leading-6" key={key}>
                                        <CheckIcon className="mt-1 mr-4 h-3.5 w-4 flex-shrink-0" /> {item}
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="mb-6 flex content-start">
                            <div className={productDetailCodeTwClass} data-testid={`${TEST_IDENTIFIER}-code`}>
                                {t('Product code')} {product.catalogNumber}
                            </div>
                            {product.brand && (
                                <div className={twJoin(productDetailCodeTwClass, 'ml-6 vl:ml-9')}>
                                    {`${t('Product line')}: `}
                                    <ExtendedNextLink type="brand" href={product.brand.slug} className="text-primary">
                                        {product.brand.name}
                                    </ExtendedNextLink>
                                </div>
                            )}
                        </div>
                        {'colorVariants' in product &&
                            'currentColorVariant' in product &&
                            product.currentColorVariant !== null && (
                                <ColorVariants
                                    currentVariantUuid={product.currentColorVariant.uuid}
                                    colorVariants={product.colorVariants}
                                />
                            )}
                        <div className="-mt-2 mb-7 -ml-2 flex vl:mb-6">
                            {product.reviewsSummary.totalCount > 0 && (
                                <Anchor
                                    onClickHandler={() => onAnchorClickHandler(reviewsRef, setIsOpenReviews)}
                                    text={<ReviewSummaryPreview summary={product.reviewsSummary} />}
                                />
                            )}
                            <Anchor
                                onClickHandler={() => onAnchorClickHandler(toTopContentRef, setIsOpenOverview)}
                                text={t('Description')}
                            />
                        </div>
                        {!product.isSaleExclusion && isB2cDomain(domainConfig) && (
                            <ProductDetailAvailability product={product} scrollTarget={scrollTarget} />
                        )}
                        {!product.isSaleExclusion && isB2bDomain(domainConfig) && (
                            <ProductStock
                                minimumSellingQuantity={product.b2bMinimumProductOrder ?? 1}
                                quantity={product.stockQuantity}
                                supplyDate={product.supplyDate}
                                className="mb-5 text-h6 font-bold leading-6"
                            />
                        )}
                        {!product.isSaleExclusion && domainType !== 'web' && (
                            <ProductDetailAddToCart gtmListName="detail" product={product} />
                        )}
                        {product.isSaleExclusion && domainType !== 'web' && (
                            <div className="font-bold text-red">{t('This product is no longer sold')}</div>
                        )}

                        {domainType === 'web' && (isItalianLocale || isEnglishLocale) && (
                            <ProductDetailBuyInEshop product={product} />
                        )}

                        <ProductWishlistButton
                            className="my-7 mb-0"
                            isWithText
                            isProductInWishlist={isProductInWishlist(product.uuid)}
                            toggleProductInWishlist={() => toggleProductInWishlist(product.uuid)}
                        />

                        <ProductDetailAdvertBanner />
                    </div>
                </div>
                {product.pictogramParameters !== null && (
                    <div className="-mt-5 mb-14 -ml-5 flex flex-wrap">
                        {product.pictogramParameters.map((pictogramParameter) => (
                            <div
                                className="mt-5 ml-5 flex flex-1 flex-row items-center text-center text-small font-normal leading-4 tracking-wider lg:w-[90px] lg:flex-none lg:flex-shrink-0 lg:flex-col"
                                key={pictogramParameter.uuid}
                            >
                                {pictogramParameter.slug ? (
                                    <ExtendedNextLink
                                        type="static"
                                        target="_blank"
                                        href={pictogramParameter.slug}
                                        className="flex flex-col items-center text-black no-underline"
                                    >
                                        <>
                                            <div className="mr-4 h-11 w-11 flex-shrink-0 lg:mr-2 [&>div]:p-0">
                                                <Image
                                                    src={pictogramParameter.image}
                                                    alt={pictogramParameter.name}
                                                    width="44"
                                                    height="44"
                                                />
                                            </div>
                                            {pictogramParameter.name}
                                        </>
                                    </ExtendedNextLink>
                                ) : (
                                    <>
                                        <div className="mr-4 h-11 w-11 flex-shrink-0 lg:mr-2 [&>div]:p-0">
                                            <Image
                                                src={pictogramParameter.image}
                                                alt={pictogramParameter.name}
                                                width="44"
                                                height="44"
                                            />
                                        </div>
                                        {pictogramParameter.name}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Webline>
            <StickyBar isSticky={isProductBarSticky} stickyRef={stickyRef} dataTestId={`${TEST_IDENTIFIER}-sticky-bar`}>
                <Webline className={twJoin(isProductBarSticky && 'pt-0.5 pb-0.5')}>
                    <ProductDetailStickyBar
                        product={product}
                        isSticky={isProductBarSticky}
                        onAddToCart={onAddToCartHandler}
                        isAddToCartFetching={isAddToCartFetching}
                    >
                        <div className="mb-0 hidden gap-y-1.5 vl:flex vl:max-w-[calc(100%-440px)] vl:flex-wrap">
                            {isProductBarSticky && (
                                <div
                                    className="mr-2 inline-flex h-8 w-8 cursor-pointer flex-row items-center justify-center rounded-2xl bg-gray text-[13px] font-bold leading-4 tracking-wide hover:bg-grayMidDarker"
                                    onClick={() => onAnchorClickHandler(toTopContentRef)}
                                >
                                    <ArrowIcon className="rotate-180" />
                                </div>
                            )}
                            <Anchor
                                onClickHandler={() => onAnchorClickHandler(toTopContentRef, setIsOpenOverview)}
                                text={t('Description and parameters')}
                            />
                            {product.files.length > 0 && (
                                <Anchor
                                    onClickHandler={() => onAnchorClickHandler(filesRef, undefined, 20)}
                                    text={t('Manual')}
                                />
                            )}
                            {product.reviewsSummary.totalCount > 0 && (
                                <Anchor
                                    onClickHandler={() => onAnchorClickHandler(reviewsRef, setIsOpenReviews)}
                                    text={
                                        <ReviewSummaryPreview
                                            summary={product.reviewsSummary}
                                            textType={isProductBarSticky ? 'countOnly' : 'fullText'}
                                        />
                                    }
                                />
                            )}
                            {product.relatedProducts.length > 0 && (
                                <Anchor
                                    onClickHandler={() => onAnchorClickHandler(relatedProductsRef, undefined, 20)}
                                    text={t('You might also like it')}
                                />
                            )}
                            {product.spareParts.length > 0 && (
                                <Anchor
                                    onClickHandler={() => onAnchorClickHandler(sparePartsRef, undefined, 20)}
                                    text={t('Spare parts')}
                                />
                            )}

                            {product.brand && (
                                <Anchor
                                    onClickHandler={() => onAnchorClickHandler(productLineRef, undefined, 20)}
                                    text={t('Product line')}
                                />
                            )}
                        </div>
                    </ProductDetailStickyBar>
                </Webline>
            </StickyBar>
            <div ref={toTopContentRef}></div>
            <div className="mb-5 pb-12">
                <Webline data-testid={`${TEST_IDENTIFIER}-description`}>
                    <Collapse
                        data-testid={`${TEST_IDENTIFIER}-overview-content`}
                        mobileTitle={t('Description and parameters')}
                        parentCounter={heightUpdateCounter}
                        parentRef={overviewRef}
                        parentState={isOpenOverview}
                        setParentState={setIsOpenOverview}
                    >
                        <div className="flex flex-col items-start vl:flex-row">
                            <div className="mb-10 vl:mb-0 vl:w-[calc(100%-460px)]">
                                <LimitHeightWrap
                                    buttonClassName="translate-y-12"
                                    buttonAlign="center"
                                    isWithButtonIcons
                                    buttonLabelLess={t('Read less')}
                                    buttonLabelMore={t('Full description')}
                                    increaseParentCounter={() => setHeightUpdateCounter((prev) => prev + 1)}
                                    mobileHeight={570}
                                    isDefaultEnabled={false}
                                >
                                    <UserText
                                        htmlContent={
                                            isB2bDomain(domainConfig)
                                                ? removeLinks(product.description)
                                                : product.description
                                        }
                                    />
                                    <YouTubeVideo youtubeVideoId={product.youtubeVideoId} />
                                </LimitHeightWrap>
                            </div>
                            <div className="mb-7 w-full vl:ml-14 vl:w-[460px] vl:rounded-md vl:border-5 vl:border-grayLight vl:p-10">
                                {product.parameterImage !== null && (
                                    <div className="h-[340px] pb-10 vl:h-[400px] vl:pb-5">
                                        <Image src={product.parameterImage} width="340" height="400" alt="" />
                                    </div>
                                )}

                                {product.groupedParameters.length > 0 && (
                                    <div className="-mt-9">
                                        {product.groupedParameters.map((group, index) => (
                                            <React.Fragment key={index}>
                                                <div className="mt-9 mb-4 text-h6 font-medium leading-6">
                                                    {group.name}
                                                </div>
                                                <Table>
                                                    <tbody>
                                                        {group.parameters
                                                            .filter((parameter) =>
                                                                parameter.values.some((value) => value.text),
                                                            )
                                                            .map((parameter) => (
                                                                <tr key={parameter.uuid}>
                                                                    <td>{parameter.name}</td>
                                                                    <th>
                                                                        {parameter.values.map((value, index) =>
                                                                            formatParameterValue(
                                                                                value.text ?? '',
                                                                                parameter.isLogicalType,
                                                                                index,
                                                                                t,
                                                                            ),
                                                                        )}
                                                                    </th>
                                                                </tr>
                                                            ))}
                                                    </tbody>
                                                </Table>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </Collapse>
                </Webline>
                {product.files.length > 0 && (
                    <Webline data-testid={`${TEST_IDENTIFIER}-manuals`}>
                        <Collapse
                            mobileTitle={t('Product manuals')}
                            parentRef={filesRef}
                            parentState={isOpenProductFiles}
                            setParentState={setIsOpenProductFiles}
                            data-testid={`${TEST_IDENTIFIER}-manuals-content`}
                        >
                            <div className="vl:pt-8">
                                <div className="bg-white vl:bg-grayLight vl:pb-8 vl:pt-8 vl:pl-12 vl:pr-12">
                                    <ProductDetailFiles files={product.files} />
                                </div>
                            </div>
                        </Collapse>
                    </Webline>
                )}
                {product.reviewsSummary.totalCount > 0 && (
                    <Webline>
                        <Collapse
                            mobileTitle={t('Reviews')}
                            parentRef={reviewsRef}
                            parentState={isOpenReviews}
                            setParentState={setIsOpenReviews}
                            parentCounter={reviewsHeightUpdateCounter}
                        >
                            <div className="vl:pt-8">
                                <div className="bg-white vl:bg-grayLight vl:pb-8 vl:pt-8 vl:pl-12 vl:pr-12">
                                    <ProductReviews
                                        summary={product.reviewsSummary}
                                        productUuid={product.uuid}
                                        increaseParentHeightCounter={() =>
                                            setReviewsHeightUpdateCounter((prev) => prev + 1)
                                        }
                                    />
                                </div>
                            </div>
                        </Collapse>
                    </Webline>
                )}
                {product.relatedProducts.length > 0 && (
                    <Webline parentRef={relatedProductsRef} data-testid={`${TEST_IDENTIFIER}-relatedProducts`}>
                        <div className="pt-8 vl:pl-12 vl:pr-12">
                            <ProductDetailRelated relatedProducts={product.relatedProducts} />
                        </div>
                    </Webline>
                )}
                {product.spareParts.length > 0 && (
                    <Webline data-testid={`${TEST_IDENTIFIER}-spareParts`}>
                        <Collapse
                            mobileTitle={t('Spare parts')}
                            parentRef={sparePartsRef}
                            parentState={isOpenProductSpareParts}
                            setParentState={setIsOpenProductSpareParts}
                            data-testid={`${TEST_IDENTIFIER}-spareParts`}
                        >
                            <div
                                className={twJoin(product.relatedProducts.length === 0 && 'pt-8', 'vl:pl-12 vl:pr-12')}
                            >
                                <ProductDetailSpareParts spareParts={product.spareParts} />
                            </div>
                        </Collapse>
                    </Webline>
                )}
                {product.brand && (
                    <Webline>
                        <Collapse
                            mobileTitle={t('Product line')}
                            parentRef={productLineRef}
                            parentState={isOpenProductLine}
                            setParentState={setIsOpenProductLine}
                            parentCounter={productLineHeightUpdateCounter}
                        >
                            <div
                                className={twJoin(
                                    product.spareParts.length + product.relatedProducts.length === 0 && 'vl:pt-8',
                                )}
                            >
                                <div className="bg-white vl:bg-grayLight vl:pb-8 vl:pt-8 vl:pl-12 vl:pr-12">
                                    <ProductLineWrapper
                                        increaseParentHeightCounter={() =>
                                            setProductLineHeightUpdateCounter((prev) => prev + 1)
                                        }
                                        parentRef={productLineRef}
                                        brand={product.brand}
                                        productDetailUuid={product.uuid}
                                    />
                                </div>
                            </div>
                        </Collapse>
                    </Webline>
                )}
            </div>
            <div id={isPersooAllowed ? PERSOO_IDS.detailAlternatives : undefined} />
        </Fragment>
    );
};

const formatParameterValue = (valueText: string, isLogicalType: boolean, index: number, t: Translate) => {
    const value = isLogicalType ? getTranslateFromLogicalValue(valueText, t) : valueText;

    return index > 0 ? ' | ' + value : value;
};

export default ProductDetail;
